import { useState, useEffect } from "react";
import axios from "axios";
import { Badge } from "@/components/ui/badge";
import { RefreshCw, ArrowRight, CheckCircle2, XCircle, Undo2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

const API = '/api';

const MODE_COLORS = {
  FULL: "bg-[#0F6CBD] text-white",
  SHORT: "bg-[#D83B01] text-white",
  NONE: "bg-[#F3F2F1] text-[#5E5E5E]",
};

const DELIVERY_STATUS = {
  released:       { label: "Delivered",  color: "text-[#107C10] bg-[#DFF6DD]", Icon: CheckCircle2 },
  release_failed: { label: "Failed",     color: "text-[#A4262C] bg-[#FDE7E9]", Icon: XCircle },
  recalled:       { label: "Recalled",   color: "text-[#5E5E5E] bg-[#F3F2F1]", Icon: Undo2 },
};

function timeAgo(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const secs = Math.max(1, Math.floor((Date.now() - d.getTime()) / 1000));
  if (secs < 60)        return `${secs}s ago`;
  if (secs < 3600)      return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400)     return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deliveries, setDeliveries] = useState([]);
  const [deliveriesLoading, setDeliveriesLoading] = useState(true);
  const navigate = useNavigate();

  const fetchStats = () => {
    setLoading(true);
    axios.get(`${API}/dashboard/stats`)
      .then((r) => { setStats(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  };

  const fetchDeliveries = () => {
    setDeliveriesLoading(true);
    axios.get(`${API}/relay/recent?limit=5`)
      .then((r) => { setDeliveries(r.data?.emails || []); })
      .catch(() => setDeliveries([]))
      .finally(() => setDeliveriesLoading(false));
  };

  useEffect(() => {
    fetchStats();
    fetchDeliveries();
    const i = setInterval(fetchDeliveries, 30000);
    return () => clearInterval(i);
  }, []);

  if (loading) {
    return (
      <div className="p-8 flex items-center gap-2 text-sm text-[#5E5E5E]">
        <RefreshCw size={16} className="animate-spin" /> Loading dashboard...
      </div>
    );
  }

  if (!stats) {
    return <div className="p-8 text-sm text-[#A4262C]">Failed to load dashboard data.</div>;
  }

  const cards = [
    { label: "Directory Users", value: stats.graph_users, accent: "#0F6CBD" },
    { label: "Templates", value: stats.templates, accent: "#107C10" },
    { label: "Emails Processed", value: stats.emails_processed, accent: "#D83B01" },
    { label: "Active Campaigns", value: stats.campaigns_active, accent: "#5E5E5E" },
  ];

  return (
    <div className="p-8 max-w-[1600px]" data-testid="dashboard-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight text-[#171717]">Dashboard</h1>
          <p className="text-sm text-[#5E5E5E] mt-1">Email signature management overview</p>
        </div>
        <button
          onClick={() => { fetchStats(); fetchDeliveries(); }}
          data-testid="refresh-stats-btn"
          className="flex items-center gap-2 bg-[#F3F2F1] text-[#171717] hover:bg-[#E1DFDD] rounded-sm px-3 py-2 text-sm font-medium transition-colors"
        >
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6" data-testid="stats-grid">
        {cards.map((card) => (
          <div key={card.label} className="bg-white border border-[#E1DFDD] rounded-sm p-5">
            <p className="text-xs font-semibold tracking-[0.05em] uppercase text-[#5E5E5E]">{card.label}</p>
            <p className="text-3xl font-heading font-semibold mt-2" style={{ color: card.accent }}>
              {card.value}
            </p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6">
        <div className="lg:col-span-2 bg-white border border-[#E1DFDD] rounded-sm">
          <div className="flex items-center justify-between px-5 py-3 border-b border-[#E1DFDD]">
            <h2 className="font-heading text-base font-medium text-[#171717]">Recent Activity</h2>
            <button
              onClick={() => navigate("/audit-log")}
              className="text-xs text-[#0F6CBD] hover:underline flex items-center gap-1"
              data-testid="view-all-activity-btn"
            >
              View all <ArrowRight size={12} />
            </button>
          </div>
          <div className="p-5">
            {stats.recent_activity.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-sm text-[#5E5E5E]">No emails processed yet.</p>
                <button
                  onClick={() => navigate("/processor")}
                  className="mt-3 text-sm text-[#0F6CBD] hover:underline flex items-center gap-1 mx-auto"
                  data-testid="go-to-processor-btn"
                >
                  Try the Test Processor <ArrowRight size={12} />
                </button>
              </div>
            ) : (
              <table className="w-full text-sm text-left" data-testid="recent-activity-table">
                <thead>
                  <tr className="text-xs uppercase text-[#5E5E5E] border-b border-[#E1DFDD]">
                    <th className="pb-2 font-semibold">Time</th>
                    <th className="pb-2 font-semibold">From</th>
                    <th className="pb-2 font-semibold">Subject</th>
                    <th className="pb-2 font-semibold">Mode</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.recent_activity.map((log) => (
                    <tr key={log.id} className="border-b border-[#E1DFDD] last:border-0 text-[#5E5E5E]">
                      <td className="py-2.5 pr-3 text-xs whitespace-nowrap">
                        {new Date(log.timestamp).toLocaleString()}
                      </td>
                      <td className="py-2.5 pr-3 font-mono text-xs">{log.from_address}</td>
                      <td className="py-2.5 pr-3 truncate max-w-[200px]">{log.subject}</td>
                      <td className="py-2.5">
                        <span className={`inline-block px-2 py-0.5 rounded-sm text-xs font-medium ${MODE_COLORS[log.mode] || ""}`}>
                          {log.mode}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <div className="bg-white border border-[#E1DFDD] rounded-sm">
          <div className="px-5 py-3 border-b border-[#E1DFDD]">
            <h2 className="font-heading text-base font-medium text-[#171717]">Quick Info</h2>
          </div>
          <div className="p-5 space-y-4 text-sm">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.05em] text-[#5E5E5E]">Brands</p>
              <p className="text-lg font-heading font-semibold text-[#171717] mt-1">{stats.brands}</p>
            </div>
            <div className="border-t border-[#E1DFDD] pt-4">
              <p className="text-xs font-semibold uppercase tracking-[0.05em] text-[#5E5E5E]">Components</p>
              <p className="text-lg font-heading font-semibold text-[#171717] mt-1">{stats.components}</p>
            </div>
            <div className="border-t border-[#E1DFDD] pt-4">
              <p className="text-xs font-semibold uppercase tracking-[0.05em] text-[#5E5E5E]">Assets</p>
              <p className="text-lg font-heading font-semibold text-[#171717] mt-1">{stats.assets}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white border border-[#E1DFDD] rounded-sm mt-6" data-testid="last-deliveries-card">
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#E1DFDD]">
          <div>
            <h2 className="font-heading text-base font-medium text-[#171717]">Last 5 Deliveries</h2>
            <p className="text-xs text-[#5E5E5E] mt-0.5">Live health pulse from the relay — auto-refreshes every 30s</p>
          </div>
          <button
            onClick={() => navigate("/outbox")}
            className="text-xs text-[#0F6CBD] hover:underline flex items-center gap-1"
            data-testid="view-outbox-btn"
          >
            Open Outbox <ArrowRight size={12} />
          </button>
        </div>
        <div className="p-5">
          {deliveriesLoading && deliveries.length === 0 ? (
            <div className="flex items-center gap-2 text-sm text-[#5E5E5E] py-4">
              <RefreshCw size={14} className="animate-spin" /> Loading deliveries...
            </div>
          ) : deliveries.length === 0 ? (
            <p className="text-sm text-[#5E5E5E] py-4">No deliveries yet — held-queue is empty.</p>
          ) : (
            <table className="w-full text-sm text-left" data-testid="last-deliveries-table">
              <thead>
                <tr className="text-xs uppercase text-[#5E5E5E] border-b border-[#E1DFDD]">
                  <th className="pb-2 font-semibold">Status</th>
                  <th className="pb-2 font-semibold">Recipient</th>
                  <th className="pb-2 font-semibold">Subject</th>
                  <th className="pb-2 font-semibold">Mode</th>
                  <th className="pb-2 font-semibold text-right">When</th>
                </tr>
              </thead>
              <tbody>
                {deliveries.slice(0, 5).map((d) => {
                  const meta = DELIVERY_STATUS[d.status] || DELIVERY_STATUS.recalled;
                  const Icon = meta.Icon;
                  const when = d.released_at || d.recalled_at || d.created_at;
                  const rcpt = (d.rcpt_tos && d.rcpt_tos[0]) || "—";
                  const extraCount = (d.rcpt_tos?.length || 1) - 1;
                  return (
                    <tr
                      key={d.hold_id}
                      className="border-b border-[#E1DFDD] last:border-0 text-[#5E5E5E]"
                      data-testid={`delivery-row-${d.hold_id}`}
                      title={d.status === "release_failed" && d.release_error ? `SMTP error: ${d.release_error}` : undefined}
                    >
                      <td className="py-2.5 pr-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-xs font-medium ${meta.color}`}>
                          <Icon size={12} /> {meta.label}
                          {d.release_error_code ? <span className="ml-0.5 opacity-70">{d.release_error_code}</span> : null}
                        </span>
                      </td>
                      <td className="py-2.5 pr-3 font-mono text-xs">
                        {rcpt}
                        {extraCount > 0 && (
                          <span className="ml-1 text-[#5E5E5E]">+{extraCount}</span>
                        )}
                      </td>
                      <td className="py-2.5 pr-3 truncate max-w-[260px]">{d.subject || "(no subject)"}</td>
                      <td className="py-2.5 pr-3">
                        {d.mode && (
                          <span className={`inline-block px-2 py-0.5 rounded-sm text-xs font-medium ${MODE_COLORS[d.mode] || ""}`}>
                            {d.mode}
                          </span>
                        )}
                      </td>
                      <td className="py-2.5 text-right text-xs whitespace-nowrap" title={when ? new Date(when).toLocaleString() : ""}>
                        {timeAgo(when)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
